<template>
  <global-modal name="event-modal-users">
    <Modal @close="$modal.hide('event-modal-users')">
      <Popup
        remove
        v-if="isRemoveAllMessage"
        @close="isRemoveAllMessage = false"
        @confirm="
          removeAllUsers();
          isRemoveAllMessage = false;
        "
      >
        {{ $t("event.users.modal.removeAllMessage") }}
      </Popup>
      <div class="event-modal-users">
        <header class="event-modal-users__header">
          <div class="event-modal-users__header-container">
            <h1 class="event-modal-users__header-title">
              {{ $t("event.users.title") }}
            </h1>

            <div class="event-modal-users__header-count">
              {{ $t("event.users.total") }} {{ users.length }}
              {{ users.length | pluralize($t("event.users.pluralize")) }}
            </div>
          </div>
        </header>

        <form
          @submit.prevent="onAddFormSubmit"
          class="event-modal-users__add"
          v-if="hasUserAdminRights && (isAddFormShown || !users.length)"
        >
          <div class="event-modal-users__add-title">
            {{ $t("event.users.modal.addUserTitle") }}

            <button
              class="event-modal-users__add-title-close"
              v-if="isAddFormShown && users.length"
              @click.prevent="isAddFormShown = false"
            >
              <icon name="hide" />
            </button>
          </div>

          <div class="event-modal-users__add-form">
            <InputLabel class="event-modal-users__add-form-email">
              <template #label>
                E-mail
              </template>

              <input
                type="email"
                v-model="addForm.email"
                name="email"
                v-validate="'required|email'"
                placeholder="name@example.ru"
              />

              <template
                v-if="errors.has('email') || addForm.error"
                slot="error"
              >
                {{ errors.first("name") }} {{ addForm.error }}
              </template>
            </InputLabel>

            <div class="event-modal-users__add-form-access">
              <div class="event-modal-users__add-form-access-label">
                {{ $t("event.users.modal.userAccessFieldTitle") }}
              </div>
              <div class="event-modal-users__add-form-access-wrapper">
                <RadioButton v-model="addForm.access" rValue="read">
                  {{ $t("event.users.modal.userAccessRead") }}
                </RadioButton>

                <RadioButton v-model="addForm.access" rValue="edit">
                  {{ $t("event.users.modal.userAccessWrite") }}
                </RadioButton>

                <RadioButton v-model="addForm.access" rValue="admin">
                  {{ $t("event.users.modal.adminAccessWrite") }}
                </RadioButton>
                <RadioButton
                  v-if="isAllowedAddInstallerRole"
                  v-model="addForm.access"
                  rValue="installer"
                >
                  {{ $t("event.users.modal.installer") }}
                </RadioButton>
              </div>
            </div>
          </div>
          <p class="event-modal-users__add-footer-description">
            {{ roleDescription }}
          </p>
          <div class="event-modal-users__add-footer">
            <PrimaryButton
              type="submit"
              :disabled="errors.any() || !addForm.email || isAddFormSubmitting"
            >
              <template v-if="isAddFormSubmitting">
                {{ $t("event.users.modal.submittingBtn") }}
              </template>

              <template v-else>
                {{ $t("event.users.modal.submitBtn") }}
              </template>
            </PrimaryButton>

            <p
              class="event-modal-users__add-footer-hint"
              v-html="$t('event.users.modal.addHelpMessage')"
            ></p>
          </div>
        </form>

        <div
          class="event-modal-users__list"
          v-if="users.length"
          :class="{ 'event-modal-users__list_blocked': isUsersListBlocked }"
        >
          <svg
            class="event-modal-users__list-loader"
            v-if="isUsersListBlocked"
            id="loader-1"
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            width="40px"
            height="40px"
            viewBox="0 0 50 50"
            style="enable-background:new 0 0 50 50;"
            xml:space="preserve"
          >
            <path
              d="M25.251,6.461c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615V6.461z"
            >
              <animateTransform
                attributeType="xml"
                attributeName="transform"
                type="rotate"
                from="0 25 25"
                to="360 25 25"
                dur="0.6s"
                repeatCount="indefinite"
              />
            </path>
          </svg>

          <div
            class="event-modal-users__list-header"
            v-if="hasUserAdminRights && !isAddFormShown"
          >
            <span @click="isAddFormShown = true">
              <LinkIcon icon="add">
                {{ $t("event.users.modal.addUserBtn") }}
              </LinkIcon>
            </span>

            <button
              class="event-modal-users__list-header-remove-all"
              @click="isRemoveAllMessage = true"
              v-if="countNonAdminUsers > 0"
            >
              {{ $t("event.users.modal.removeAllBtn") }}
            </button>
          </div>

          <div
            class="event-modal-users__list-item"
            v-for="user in users"
            :key="user.id"
          >
            <template>
              <div
                class="event-modal-users__list-item-name"
                :title="user.email"
              >
                {{ user.lastname }} {{ user.name }} {{ user.middlename }}<br />
                <span class="event-modal-users__list-item-name--email">{{
                  user.email
                }}</span>
              </div>

              <template v-if="['supervisor'].includes(user.role)">
                <div class="event-modal-users__list-item-access">
                  {{ $t("event.users.modal.admin") }}
                </div>
              </template>

              <template v-else>
                <Select
                  :value="user.role"
                  @input="updateUserAccess(user, $event)"
                  class="event-modal-users__list-item-access"
                  :options="userRoles"
                  :disabled="!hasUserAdminRights"
                />
              </template>
            </template>
            <div class="event-modal-users__list-item-delete__wrap">
              <button
                class="event-modal-users__list-item-delete"
                @click="removeUser(user)"
                v-if="hasUserAdminRights && !['supervisor'].includes(user.role)"
              >
                <Tooltip :label="$t('event.documents.deleteFile')">
                  <icon name="delete" />
                </Tooltip>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </global-modal>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import apiParticipant from "@/api/participant";
import Modal from "@/components/Modal/Modal.vue";
import {
  PrimaryButton,
  LinkIcon,
  Tooltip,
  RadioButton,
  InputLabel,
  Select,
  Popup
} from "@v2/shared/ui/components";
import getError from "@/helpers/error";

export default {
  name: "EventModalUsers",
  components: {
    Popup,
    Tooltip,
    LinkIcon,
    PrimaryButton,
    RadioButton,
    InputLabel,
    Modal,
    Select
  },

  data() {
    return {
      addForm: {
        email: "",
        access: "read",
        error: ""
      },
      isAddFormShown: false,
      isAddFormSubmitting: false,
      isUsersListBlocked: false,
      isAccessUpdateFail: false,

      isRemoveAllMessage: false
    };
  },

  computed: {
    ...mapGetters("event", ["hasUserAdminRights", "eventId"]),
    ...mapState("event", ["event"]),

    users() {
      return this.event?.users || [];
    },

    countNonAdminUsers() {
      return this.users.filter(
        user => !["supervisor", "admin"].includes(user.role)
      ).length;
    },

    isAllowedAddInstallerRole() {
      return ["self", "collective", "sub"].includes(this.event?.type);
    },
    roleDescription() {
      const access = this.addForm.access;
      if (access === "admin") {
        return this.$t("event.users.modal.adminRoleDescription");
      } else if (access === "read") {
        return this.$t("event.users.modal.readRoleDescription");
      } else if (access === "edit") {
        return this.$t("event.users.modal.editRoleDescription");
      } else if (access === "installer") {
        return this.$t("event.users.modal.installRoleDescription");
      }
      return "";
    },
    userRoles() {
      return ["admin", "read", "edit", "installer"].map(item => {
        return {
          value: item,
          title: this.$t(`event.users.modal.${item}`)
        };
      });
    }
  },

  methods: {
    ...mapMutations("event", ["UPDATE_USER_STATUS", "ADD_USER", "REMOVE_USER"]),

    async onAddFormSubmit() {
      try {
        const result = await this.$validator.validateAll();

        if (!result) {
          return;
        }

        this.isAddFormSubmitting = true;

        const participantUserResponse = await apiParticipant.createParticipantUser(
          this.eventId,
          {
            email: this.addForm.email,
            role: this.addForm.access
          }
        );
        if (participantUserResponse?.data) {
          await this.$store.dispatch(
            "event/addUser",
            participantUserResponse.data
          );
        }
        this.addForm.email = "";
        this.addForm.access = "read";

        await this.$validator.reset();

        this.isAddFormSubmitting = false;
        this.isAddFormShown = false;

        await this.$store.dispatch(
          "common/setErrorMessage",
          this.$t("event.users.modal.addUserSuccessMessage")
        );
      } catch (e) {
        await this.$store.dispatch("common/setErrorMessage", getError(e));
        this.isAddFormSubmitting = false;
      }
    },

    removeUser(user) {
      apiParticipant.deleteParticipantUser(this.eventId, user.id);
      this.$store.dispatch("event/removeUser", user.id);
    },

    async removeAllUsers() {
      this.isUsersListBlocked = true;

      const usersToRemove = this.users.filter(
        x => !["supervisor", "admin"].includes(x.role)
      );

      await Promise.all(
        usersToRemove.map(async user => {
          await apiParticipant.deleteParticipantUser(this.eventId, user.id);
          this.$store.dispatch("event/removeUser", user.id);
        })
      );

      this.isUsersListBlocked = false;
    },

    async updateUserAccess(user, role) {
      try {
        const result = await apiParticipant.updateParticipantUser(
          this.eventId,
          user.id,
          {
            role
          }
        );
        await this.$store.dispatch("event/updateUser", result.data);
      } catch (e) {
        await this.$store.dispatch("common/setErrorMessage", getError(e));
      }
    }
  },
  watch: {
    "addForm.email"() {
      this.addForm.error = "";
    },

    "addForm.access"() {
      this.addForm.error = "";
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-users {
  min-height: 100vh;
  background: #f9f9f9;

  display: flex;
  flex-direction: column;

  &__header {
    &-container {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;

      width: 780px;

      box-sizing: border-box;

      @include respond-down(lg) {
        width: 100%;
      }

      @include respond-down(md) {
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
      }
    }

    padding: 55px 170px 33px 80px;

    background: #fff;

    @include respond-down(lg) {
      padding: 47px 24px 33px 24px;
    }

    @include respond-down(md) {
      padding: 24px 16px 32px 16px;
    }

    &-title {
      @include h1();
    }

    &-count {
      font-size: 16px;
      font-weight: 500;
      line-height: 1.7;
      color: #505050;

      @include respond-down(md) {
        font-size: 14px;
        margin-top: 8px;
      }
    }
  }

  &__add {
    border-top: 2px solid #f1f1f1;
    border-bottom: 2px solid #f1f1f1;

    padding: 40px 0 62px 81px;

    @include respond-down(lg) {
      padding: 40px 24px 62px 24px;
    }

    @include respond-down(md) {
      padding: 30px 16px 32px 16px;
    }

    &-title {
      @include h2();

      display: flex;
      align-items: center;
      justify-content: space-between;

      width: 780px;

      @include respond-down(lg) {
        width: 100%;
      }

      @include respond-down(md) {
        width: 100%;
      }

      &-close {
        padding: 0;
        background: none;
        border: none;
        outline: none;
        cursor: pointer;

        .svg-icon {
          width: 16px;
          height: 16px;
        }
      }
    }

    &-form {
      display: flex;
      margin-top: 38px;

      width: 780px;

      @include respond-down(lg) {
        width: 100%;
      }

      @include respond-down(md) {
        flex-direction: column;
        width: 100%;
        margin-top: 16px;
      }

      &-email {
        width: 350px;
        margin-right: 50px;

        @include respond-down(lg) {
          margin-right: 40px;
        }

        @include respond-down(md) {
          width: 100%;
          margin: 0 0 24px 0;
        }
      }

      &-access {
        @include respond-down(md) {
          display: flex;
          flex-wrap: wrap;
        }
        &-wrapper {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }
        &-label {
          font-size: 12px;
          line-height: 1.33;
          color: #6d7179;
          margin-bottom: 16px;

          @include respond-down(md) {
            width: 100%;
            margin: 0 0 8px 0;
          }
        }
      }
    }

    &-footer {
      display: flex;
      align-items: center;
      margin-top: 40px;

      width: 780px;

      @include respond-down(lg) {
        width: 100%;
      }

      @include respond-down(md) {
        width: 100%;
        margin-top: 24px;

        display: flex;
        flex-direction: column;

        align-items: flex-start;
      }

      &-hint {
        font-size: 12px;
        line-height: 1.33;
        color: #6d7179;
        margin: 0 0 0 32px;

        @include respond-down(md) {
          margin: 16px 0 0 0;
          font-size: 11px;
          max-width: 227px;

          br {
            display: none;
          }
        }
      }
      &-description {
        @include description();
        max-width: 700px;
        padding-top: 10px;
      }
    }
  }

  &__list {
    flex-grow: 1;

    padding: 40px 0 100px 80px;

    background: #fff;

    &_blocked {
      position: relative;

      pointer-events: none;
      touch-action: none;

      &::after {
        content: "";
        display: block;

        width: 100%;
        height: 100%;

        position: absolute;
        left: 0;
        top: 0;

        background: rgba(0, 0, 0, 0.05);
      }
    }

    &-loader {
      @include centerAbsolutely();
    }

    @include respond-down(lg) {
      padding: 0 24px 40px 24px;
    }

    @include respond-down(md) {
      padding: 0;
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;

      &-remove-all {
        background: none;
        padding: 0;
        border: none;
        outline: none;
        font-size: 12px;
        font-weight: 500;
        color: #a3a8b3;
        cursor: pointer;
      }

      margin-bottom: 26px;

      width: 780px;

      @include respond-down(lg) {
        width: 100%;
      }

      @include respond-down(md) {
        width: 100%;

        box-sizing: border-box;

        align-items: center;

        margin-bottom: 19px;
        padding: 0 16px;
      }
    }

    &-item {
      display: flex;
      align-items: center;

      height: 70px;

      border-top: 1px solid rgba(205, 206, 208, 0.5);
      border-bottom: 1px solid rgba(205, 206, 208, 0.5);

      margin-top: -1px;

      width: 780px;

      position: relative;

      @include respond-down(lg) {
        width: 100%;
      }

      @include respond-down(md) {
        width: 100%;
        height: auto;

        box-sizing: border-box;

        padding: 18px 16px;

        display: block;
      }

      &-name {
        font-size: 16px;
        line-height: 1.5;
        color: #000000;

        @include respond-down(md) {
          font-size: 12px;
          margin-bottom: 12px;
        }

        &--email {
          font-size: 14px;
          color: #a3a8b3;
          display: block;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          max-width: 500px;

          @include respond-down(md) {
            font-size: 11px;
            max-width: 300px;
          }

          @include respond-down(sm) {
            max-width: 280px;
          }

          @include respond-down(xs) {
            max-width: 200px;
          }
        }
      }

      &-resend-invite {
        margin-left: auto;
        margin-right: 65px;

        font-size: 13px;
        color: #000;

        display: flex;
        justify-content: space-between;
        align-items: center;

        width: 190px;

        .svg-icon {
          filter: grayscale(1);
        }

        @include respond-down(md) {
          margin-left: 0;
          margin-right: 0;

          width: auto;

          .svg-icon {
            margin-left: 10px;
          }
        }
      }

      &-access {
        margin-left: auto;
        margin-right: 65px;

        font-size: 14px;
        font-weight: 500;
        color: #6d7179;

        width: 190px;
        & .select__toggle {
          width: 100%;
          height: auto;
          border: transparent;
          padding: 0;
          background-color: transparent;
          &.select__toggle_disabled {
            .svg-icon {
              color: #cdced0;
            }
          }
          .svg-icon {
            color: #6d7179;
            width: 14px;
            height: 14px;
          }
        }

        & .select__toggle-text {
          color: #6d7179;
          overflow: visible;
          width: auto;
          font-weight: 500;
        }
        @include respond-down(md) {
          font-size: 12px;
          margin: 0;

          width: auto;
          display: inline-block;
        }
      }

      &-delete {
        background: none;
        border: none;
        outline: none;

        padding: 0;
        margin-right: 27px;
        position: relative;
        cursor: pointer;
        &__wrap {
          width: 45px;
        }
        .svg-icon {
          width: 16px;
          height: 16px;
        }

        @include respond-down(md) {
          position: absolute;
          right: -15px;
          top: 50%;
          transform: translateY(-50%);
        }
      }
    }
  }
}
</style>
